// The phone's position for the capture screens: nearest projects first, and the GPS a field
// photo carries. Watches with high accuracy while mounted. Watching starts on mount (the
// browser asks the first time) unless location is already blocked; `request()` starts it
// again, for the "Turn on location" button after the crew changes the setting.

import { useCallback, useEffect, useRef, useState } from "react";
import { logger } from "@/lib/logger";

type LocationStatus = "unknown" | "prompt" | "granted" | "denied" | "unavailable";

interface Position {
  lat: number;
  lng: number;
  /** Metres, as the phone reports it. */
  accuracy: number;
}

interface LocationState {
  status: LocationStatus;
  position: Position | null;
  request(): void;
}

/** A cached fix this recent is good enough; a fresh one is asked for after it. */
const MAX_AGE_MS = 10_000;
const TIMEOUT_MS = 30_000;

const hasGeolocation = () => typeof navigator !== "undefined" && "geolocation" in navigator;

export function useLocation(): LocationState {
  const [status, setStatus] = useState<LocationStatus>(() =>
    hasGeolocation() ? "unknown" : "unavailable",
  );
  const [position, setPosition] = useState<Position | null>(null);
  const watchId = useRef<number | null>(null);

  const stop = useCallback(() => {
    if (watchId.current !== null) navigator.geolocation.clearWatch(watchId.current);
    watchId.current = null;
  }, []);

  const start = useCallback(() => {
    if (!hasGeolocation()) {
      setStatus("unavailable");
      return;
    }
    stop();
    watchId.current = navigator.geolocation.watchPosition(
      (fix) => {
        setStatus("granted");
        setPosition({
          lat: fix.coords.latitude,
          lng: fix.coords.longitude,
          accuracy: fix.coords.accuracy,
        });
      },
      (error) => {
        if (error.code === error.PERMISSION_DENIED) {
          setStatus("denied");
          setPosition(null);
        } else if (error.code === error.POSITION_UNAVAILABLE) {
          setStatus("unavailable");
        }
        // A timeout keeps watching: the next fix clears it.
      },
      { enableHighAccuracy: true, maximumAge: MAX_AGE_MS, timeout: TIMEOUT_MS },
    );
  }, [stop]);

  useEffect(() => {
    if (!hasGeolocation()) return;
    let cancelled = false;
    let permission: PermissionStatus | null = null;

    const onChange = () => {
      if (!permission) return;
      setStatus(permission.state);
      if (permission.state === "granted") start();
      if (permission.state === "denied") {
        stop();
        setPosition(null);
      }
    };

    const watchUnlessBlocked = (state: PermissionState | null) => {
      if (state === "denied") setStatus("denied");
      else start();
    };

    if (navigator.permissions?.query) {
      navigator.permissions
        .query({ name: "geolocation" })
        .then((result) => {
          if (cancelled) return;
          permission = result;
          setStatus(result.state);
          result.addEventListener("change", onChange);
          watchUnlessBlocked(result.state);
        })
        .catch((error: unknown) => {
          logger.warn("location permission query failed", { error: String(error) });
          if (!cancelled) watchUnlessBlocked(null);
        });
    } else {
      watchUnlessBlocked(null);
    }

    return () => {
      cancelled = true;
      permission?.removeEventListener("change", onChange);
      stop();
    };
  }, [start, stop]);

  return { status, position, request: start };
}
